import { useMemo } from 'react';

import { Box, Flex, SimpleGrid } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useMask } from '@react-input/mask';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import {
  Checkbox,
  DatePicker,
  InputLabel,
  NumberInput,
  Select,
  Text,
  TextInput,
  TimePicker,
  Title,
  YesNoRadioButton,
} from 'components';
import { convertToTimeString } from 'components/time';
import { AppConstants } from 'constant';
import { CreditOptionsResponse, CreditRequestForm } from 'types';
import { AppUtils } from 'utils';

const { MASK_SWISS_PHONE_NUMBER } = AppConstants;

interface CreditRequestPersonalPartnerProps {
  form: UseFormReturnType<CreditRequestForm>;
  options: CreditOptionsResponse;
  visibleFields: string[];
  readOnly?: boolean;
}

const CreditRequestPersonalPartner = ({
  form,
  options,
  visibleFields,
  readOnly = false,
}: CreditRequestPersonalPartnerProps) => {
  const { t } = useTranslation();

  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });

  const nationalities = useMemo(
    () => AppUtils.getOrderedCountryList(options['nationality'], t),
    [options, t],
  );

  return (
    <Box mt={'md'}>
      <Title order={5} px={'md'} mb={'sm'} c={'var(--mantine-color-primary-6)'}>
        {t('credit.partner.label')}
      </Title>
      <Box p={'md'}>
        <SimpleGrid cols={4}>
          {visibleFields.includes('partnerOnSameAddress') && (
            <YesNoRadioButton
              label={t('credit.partnerOnSameAddress.label')}
              name={'partnerOnSameAddress'}
              form={form}
              t={t}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('partnerIncludeInApplication') && (
            <YesNoRadioButton
              label={t('credit.partnerIncludeInApplication.label')}
              name={'partnerIncludeInApplication'}
              form={form}
              t={t}
              disabled={readOnly}
            />
          )}
        </SimpleGrid>
      </Box>

      <Box p={'md'} mt={'sm'}>
        <SimpleGrid cols={4}>
          {visibleFields.includes('partnerFirstName') && (
            <TextInput
              label={t('credit.partnerFirstName.label')}
              key={form.key('partnerFirstName')}
              {...form.getInputProps('partnerFirstName')}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('partnerLastName') && (
            <TextInput
              label={t('credit.partnerLastName.label')}
              key={form.key('partnerLastName')}
              {...form.getInputProps('partnerLastName')}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('partnerGender') && (
            <Select
              label={t('credit.partnerGender.label')}
              data={['MALE', 'FEMALE', 'OTHER'].map((el) => ({
                value: el,
                label: t(`user.gender.options.${el}`),
              }))}
              key={form.key('partnerGender')}
              {...form.getInputProps('partnerGender')}
              clearable={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('partnerGender') && (
            <DatePicker
              label={t('credit.partnerBirthDate.label')}
              placeholder={t('user.birthDate.placeholder')}
              key={form.key('partnerBirthDate')}
              {...form.getInputProps('partnerBirthDate')}
              minDate={moment().subtract(65, 'years')}
              maxDate={moment().subtract(18, 'years')}
              required={true}
              disabled={readOnly}
            />
          )}
        </SimpleGrid>

        <SimpleGrid cols={4} mt={'sm'}>
          {visibleFields.includes('partnerPhoneNumber') && (
            <Box>
              <InputLabel label={t('credit.partner.phone')} />
              <Flex justify={'space-between'}>
                <NumberInput
                  key={form.key('partnerCountryCode')}
                  {...form.getInputProps('partnerCountryCode')}
                  plain={true}
                  w={'30%'}
                  disabled={readOnly}
                />
                <TextInput
                  key={form.key('partnerPhoneNumber')}
                  {...form.getInputProps('partnerPhoneNumber')}
                  w={'65%'}
                  ref={phoneNumberRef}
                  disabled={readOnly}
                />
              </Flex>
            </Box>
          )}
          {visibleFields.includes('partnerEmail') && (
            <TextInput
              label={t('credit.partnerEmail.label')}
              key={form.key('partnerEmail')}
              {...form.getInputProps('partnerEmail')}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('partnerWorkdayAvailabilityFrom') &&
            visibleFields.includes('partnerWorkdayAvailabilityTo') && (
              <Box>
                <InputLabel label={t('credit.workdayAvailability.label')} />
                <Flex align={'center'} justify={'space-evenly'}>
                  <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                    {t('credit.workdayAvailability.from')}
                  </Text>
                  <TimePicker
                    key={form.key('partnerWorkdayAvailabilityFrom')}
                    {...form.getInputProps('partnerWorkdayAvailabilityFrom')}
                    min={options['workdayAvailability'].min}
                    max={options['workdayAvailability'].max}
                    w={'30%'}
                    disabled={readOnly}
                  />
                  <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                    {t('credit.workdayAvailability.to')}
                  </Text>
                  <TimePicker
                    key={form.key('partnerWorkdayAvailabilityTo')}
                    {...form.getInputProps('partnerWorkdayAvailabilityTo')}
                    min={options['workdayAvailability'].min}
                    max={options['workdayAvailability'].max}
                    w={'30%'}
                    disabled={readOnly}
                  />
                </Flex>
                <Checkbox
                  key={form.key('partnerSaturdayAvailability')}
                  label={t('credit.saturdayAvailability.label')}
                  {...form.getInputProps('partnerSaturdayAvailability', { type: 'checkbox' })}
                  customOnClick={() => {
                    form.setFieldValue(
                      'partnerSaturdayAvailabilityFrom',
                      convertToTimeString(options['saturdayAvailability']?.min || 0),
                    );
                    form.setFieldValue(
                      'partnerSaturdayAvailabilityTo',
                      convertToTimeString(options['saturdayAvailability']?.max || 0),
                    );
                  }}
                  disabled={readOnly}
                />
              </Box>
            )}
          {visibleFields.includes('partnerSaturdayAvailabilityFrom') &&
            visibleFields.includes('partnerSaturdayAvailabilityTo') && (
              <Box>
                <InputLabel label={t('credit.saturdayAvailability.label')} />
                <Flex align={'center'} justify={'space-evenly'}>
                  <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                    {t('credit.saturdayAvailability.from')}
                  </Text>
                  <TimePicker
                    key={form.key('partnerSaturdayAvailabilityFrom')}
                    {...form.getInputProps('partnerSaturdayAvailabilityFrom')}
                    min={options['saturdayAvailability'].min}
                    max={options['saturdayAvailability'].max}
                    w={'30%'}
                    disabled={readOnly}
                  />
                  <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                    {t('credit.saturdayAvailability.to')}
                  </Text>
                  <TimePicker
                    key={form.key('partnerSaturdayAvailabilityTo')}
                    {...form.getInputProps('partnerSaturdayAvailabilityTo')}
                    min={options['saturdayAvailability'].min}
                    max={options['saturdayAvailability'].max}
                    w={'30%'}
                    disabled={readOnly}
                  />
                </Flex>
              </Box>
            )}
        </SimpleGrid>
      </Box>

      {visibleFields.includes('partnerNationality') && (
        <Box p={'md'} mt={'sm'}>
          <SimpleGrid cols={4}>
            <Select
              label={t('credit.partnerNationality.label')}
              data={nationalities}
              key={form.key('partnerNationality')}
              {...form.getInputProps('partnerNationality')}
              clearable={true}
              required={true}
              disabled={readOnly}
            />
            {visibleFields.includes('partnerVisaType') && (
              <Select
                label={t('credit.partnerVisaType.label')}
                data={options['visaType'].map((el) => ({
                  value: el,
                  label: t(`user.visa.options.${el}`),
                }))}
                key={form.key('partnerVisaType')}
                {...form.getInputProps('partnerVisaType')}
                clearable={true}
                required={true}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes('partnerVisaFrom') && (
              <DatePicker
                label={t('credit.partnerVisaType.from')}
                key={form.key('partnerVisaFrom')}
                {...form.getInputProps('partnerVisaFrom')}
                maxDate={new Date()}
                required={true}
                disabled={readOnly}
              />
            )}
          </SimpleGrid>
        </Box>
      )}
    </Box>
  );
};

export default CreditRequestPersonalPartner;
